import type { Brand } from '@mathflat/domain/Brand'
import { type HomeButtonLink, type HomeButtonLinks, noticeApi } from '@mathflat/domain/Notice'

import type { Academy } from '@/dto'
import dateUtils from '@/utils/DateUtils'

type BrandConfig = {
  linkKey: keyof Pick<HomeButtonLinks, 'homeButtonLinkMathflat' | 'homeButtonLinkSchoolflat'>
}

const BRAND_CONFIG: Record<Brand, BrandConfig> = {
  MATHFLAT: { linkKey: 'homeButtonLinkMathflat' },
  SCHOOLFLAT: { linkKey: 'homeButtonLinkSchoolflat' },
}

class HcmsLandingPathController {
  getHomeButtonLandingPath = async (brand: Brand, dCode: Academy['academyId']) => {
    const homeButtonLinksList = await noticeApi.getHomeButtonLinks()
    const activatedLandingPath = this._findActivatedLandingPath(homeButtonLinksList, brand)

    const defaultTarget = this._extractUrlFromLandingPath(activatedLandingPath, brand)
    const customTarget = this._findCustomUrlFromLandingPath(defaultTarget?.customUrls, dCode)

    return customTarget?.customUrl || defaultTarget?.url || null
  }

  private _findActivatedLandingPath(homeButtonLinksList: HomeButtonLinks[], brand: Brand) {
    const now = new Date()
    const config = BRAND_CONFIG[brand]

    return homeButtonLinksList.find((landingPath) => {
      const linkData = landingPath[config.linkKey]
      if (!linkData) return false

      return dateUtils.checkIsWithinInterval(now, {
        startDate: new Date(linkData.startDatetime),
        endDate: new Date(linkData.endDatetime),
        strictCheck: true,
      })
    })
  }

  private _extractUrlFromLandingPath(
    homeButtonLinks: HomeButtonLinks | undefined,
    brand: Brand,
  ): HomeButtonLink | null {
    if (!homeButtonLinks) return null

    const config = BRAND_CONFIG[brand]
    return homeButtonLinks[config.linkKey] ?? null
  }

  private _findCustomUrlFromLandingPath(
    customUrls: HomeButtonLink['customUrls'] | undefined,
    currentDCode: Academy['academyId'],
  ) {
    return customUrls?.find((customUrl) =>
      customUrl.Dcode.some((dcode) => dcode.children.some((child) => child.text === currentDCode)),
    )
  }
}

export const hcmsLandingPathController = new HcmsLandingPathController()
